/**
 * Component for deleting a case
 */
import React, { useState } from 'react';
import { Button, Modal } from '../../../shared/components';
import { useCases } from '../hooks/useCases';

/**
 * Props for the DeleteCase component
 */
interface DeleteCaseProps {
  /**
   * ID of the case to delete
   */
  caseId: string;
  
  /**
   * Callback function called when a case is successfully deleted
   */
  onSuccess: () => void;
}

/**
 * Component that allows users to delete a case
 */
export function DeleteCase({ caseId, onSuccess }: DeleteCaseProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { removeCase } = useCases();

  /**
   * Open the confirmation modal
   */
  const handleOpen = () => {
    setIsOpen(true);
  };

  /**
   * Close the modal and reset state
   */
  const handleClose = () => {
    setIsOpen(false);
    setError(null);
  };

  /**
   * Handle case deletion
   */
  const handleDelete = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      await removeCase(caseId);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete case');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="danger"
        onClick={handleOpen}
        startIcon={
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        }
      >
        Delete
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Delete Case"
        size="sm"
      >
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}
        
        <div className="text-gray-700">
          <p>Are you sure you want to delete this case?</p>
          <p className="mt-2 text-sm text-red-600 font-medium">This action cannot be undone.</p>
        </div>
        
        <div className="mt-6 flex justify-end space-x-3">
          <Button
            type="button"
            variant="light"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
}
